import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import axios from "axios";
import * as Models from "./models";
import * as Config from "./serverConfig";
import { FuelPrice } from "./FuelPrice.js";

export const getTruckStats = createAsyncThunk(
  "trucks/getTruckStats",
  async (props) => {
    const link = Config.Server?.link + "mode=getTruckStats";
    let dataList = [];

    const params = new URLSearchParams();
    params.append("id", props.id);
    params.append("from", props.from);
    params.append("to", props.to);
    params.append("company", process.env.REACT_APP_GPS_COMPANY);

    const response = await axios({
      method: "post",
      url: link,
      data: params,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    // const data = await response.data;

    // return data === undefined ? null : data;
    const json = await response.data;
    const fuelPrice = await FuelPrice();

    // console.log(fuelPrice)

    if (json.success) {
      dataList = json.data.map((item) => {
        item.fuel_consumption_price =
          parseFloat(item?.total_oil_consumed_percent || 0) *
            parseFloat(item?.fuel_capacity || 0) *
            fuelPrice >
          0
            ? (parseFloat(item?.total_oil_consumed_percent || 0) *
                parseFloat(item?.fuel_capacity || 0) *
                fuelPrice) /
              100
            : 0;
        item.fuel_addition_price =
          parseFloat(item?.total_oil_added_percent || 0) *
            parseFloat(item?.fuel_capacity || 0) *
            fuelPrice >
          0
            ? (parseFloat(item?.total_oil_added_percent || 0) *
                parseFloat(item?.fuel_capacity || 0) *
                fuelPrice) /
              100
            : 0;
        return new Models.TruckStatsModel(item);
      });
      return dataList;
    }
  },
);

export const getFuelConsumptionStats = createAsyncThunk(
  "trucks/getFuelConsumptionStats",
  async (props) => {
    const link = Config.Server?.link + "mode=getFuelConsumptionStats";

    const params = new URLSearchParams();
    params.append("id", props.id);
    params.append("from", props.from);
    params.append("to", props.to);
    params.append("company", process.env.REACT_APP_GPS_COMPANY);

    const response = await axios({
      method: "post",
      url: link,
      data: params,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    const json = await response.data;
    if (json.success && Array.isArray(json.data)) {
      return json.data.map((item, index) => ({
        ...item,
        id: index + 1, // Unique ID for DataGrid
      }));
    } else {
      return { success: false, message: json.message || "No data returned" };
    }
  },
);

export const getFuelEfficiencyStats = createAsyncThunk(
  "trucks/getFuelEfficiencyStats",
  async (props) => {
    const link = Config.Server?.link + "mode=getFuelEfficiencyStats";
    let dataList = [];

    const params = new URLSearchParams();
    params.append("id", props.id);
    params.append("from", props.from);
    params.append("to", props.to);
    params.append("company", process.env.REACT_APP_GPS_COMPANY);

    const response = await axios({
      method: "post",
      url: link,
      data: params,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    const json = await response.data;

    if (!json || typeof json !== "object") {
      return { success: false, message: "Invalid response from server" };
    }

    if (json.success) {
      // No special model needed for now, just return raw data or map if needed
      dataList = json.data.map((item, index) => {
        item.id = index + 1; // Unique ID for DataGrid
        return item;
      });
      return dataList;
    } else {
      return { success: false, message: json.message || "Unknown error" };
    }
  },
);

export const getMonthlyReport = createAsyncThunk(
  "trucks/getMonthlyReport",
  async (props) => {
    const link = Config.Server?.link + "mode=getMonthlyTruckStats";
    let dataList = [];

    const params = new URLSearchParams();
    params.append("id", props.id);
    params.append("from", props.from);
    params.append("to", props.to);
    params.append("company", process.env.REACT_APP_GPS_COMPANY);

    const response = await axios({
      method: "post",
      url: link,
      data: params,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    const json = await response.data;
    // const fuelPrice = await FuelPrice();
    // No fuel calculation for now as fields are missing in monthly stats

    if (json.success) {
      dataList = json.data.map((item, index) => {
        // Map month_period to date for compatibility
        item.date = item.month_period;

        // Preserve original truck ID
        item.truck_id = item.id;

        // Use index + 1 as the Row ID (unique for DataGrid)
        item.id = index + 1;

        return item;
      });
      return dataList;
    } else {
      return { success: json.success };
    }
  },
);

export const getDailyActivityReport = createAsyncThunk(
  "trucks/getDailyActivityReport",
  async (props) => {
    const link = Config.Server?.link + "mode=getDailyActivityReport";
    let dataList = [];

    const params = new URLSearchParams();
    // Fix: Ensure ID is a string even if passed as an array from the component
    const idParam = Array.isArray(props.id) ? props.id.join(",") : props.id;
    params.append("id", idParam);
    params.append("from", props.from);
    params.append("to", props.to);
    params.append("company", process.env.REACT_APP_GPS_COMPANY);

    const response = await axios({
      method: "post",
      url: link,
      data: params,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    const json = await response.data;
    console.log("DEBUG | Raw Backend Response:", json);


    if (json.success && Array.isArray(json.data)) {
      dataList = json.data.map((item, index) => {
        item.original_id = item.id;
        item.id = index + 1; // Unique ID for DataGrid
        return item;
      });
      return dataList;
    } else {
      return { success: false, message: json.message || "No data returned" };
    }
  },
);

const truckStatsAdapter = createEntityAdapter({});

export const {
  selectAll: selectTruckStatsReport,
  selectById: selectTruckStatsReportById,
} = truckStatsAdapter.getSelectors((state) => state.truckStatsReport);

const Slice = createSlice({
  name: "truckStats",
  initialState: truckStatsAdapter.getInitialState({
    searchText: "",
  }),
  reducers: {
    setTruckStatsReportSearchText: {
      reducer: (state, action) => {
        state.searchText = action.payload;
      },
      prepare: (event) => ({ payload: event.target.value || "" }),
    },
  },
  extraReducers: {
    [getTruckStats.fulfilled]: truckStatsAdapter.setAll,
    [getMonthlyReport.fulfilled]: truckStatsAdapter.setAll,
    [getFuelConsumptionStats.fulfilled]: (state, action) => {
      const data = action.payload?.data || action.payload;
      if (Array.isArray(data)) {
        truckStatsAdapter.setAll(state, data);
      }
    },
    [getDailyActivityReport.fulfilled]: (state, action) => {
      if (Array.isArray(action.payload)) {
        truckStatsAdapter.setAll(state, action.payload);
      }
    },
    [getFuelEfficiencyStats.fulfilled]: (state, action) => {
      if (Array.isArray(action.payload)) {
        truckStatsAdapter.setAll(state, action.payload);
      }
    },
    // [removeMerchants.fulfilled]: (state, action) =>
    //   trucksAdapter.removeMany(state, action.payload),
  },
});

export const { setTruckStatsReportSearchText } = Slice.actions;

export const selectTruckStatsReportSearchText = ({ truckStatsReport }) =>
  truckStatsReport.searchText;

export default Slice.reducer;
